// Reine Methodenauswahl (controlled). Den Zahlungsstart übernehmen die Aufrufer
// (BookingSummary, Retry in PaymentReturn).
import React, { FC, useRef } from "react"
import { Skeleton } from "antd"
import { CheckCircleFilled } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useGetAvailablePaymentMethodsFromEndUserAppQuery } from "../../../generated/graphql"
import { gateMollieMethodsForDevice } from "../helpers/gateMollieMethodsForDevice"
import css from "./MollieMethodsBlock.module.css"

// Lokale Fallback-Icons: werden nur genutzt, wenn die offizielle Mollie-CDN-URL
// nicht erreichbar ist (CSP-Block, Offline-Preview, seltene CDN-Ausfälle).
import creditcardIcon from "../../../assets/payment-icons/mollie/creditcard.svg"
import paypalIcon from "../../../assets/payment-icons/mollie/paypal.svg"
import banktransferIcon from "../../../assets/payment-icons/mollie/banktransfer.svg"
import directdebitIcon from "../../../assets/payment-icons/mollie/directdebit.svg"
import applepayIcon from "../../../assets/payment-icons/mollie/applepay.svg"
import googlepayIcon from "../../../assets/payment-icons/mollie/googlepay.svg"
import weroIcon from "../../../assets/payment-icons/mollie/wero.svg"

const LOCAL_FALLBACK_ICONS: Record<string, string> = {
    creditcard: creditcardIcon,
    paypal: paypalIcon,
    banktransfer: banktransferIcon,
    directdebit: directdebitIcon,
    applepay: applepayIcon,
    googlepay: googlepayIcon,
    wero: weroIcon,
}

/** Offizielle Mollie-CDN-URL für ein Method-Icon. Phase 9-UI-Hybrid: Primary. */
const mollieCdnIconUrl = (method: string): string =>
    `https://www.mollie.com/external/icons/payment-methods/${method}.svg`

type MollieMethodsBlockProps = {
    /**
     * Kurs-Kontext fuer die kursabhaengige Mollie-Methoden-Abfrage. Wenn gesetzt,
     * filtert das Backend die Methodenliste (leere Liste bei `allowMolliePayment=false`).
     * Ohne `courseId` bleibt das bisherige (ungefilterte) Verhalten erhalten —
     * Rueckwaerts-Kompatibilitaet fuer Aufrufer ohne Kurs-Kontext (z.B. Retry-Flow).
     */
    courseId?: string | null
    /** `null` = noch keine Auswahl. */
    selectedMethod: string | null
    onMethodSelect: (methodId: string | null) => void
    /**
     * Der Parent triggert das initiate selbst (onMethodSelect → handleRetryWithMethod)
     * und besitzt dessen Loading-State. `busy` reicht ihn durch, damit die Karten
     * während eines laufenden Retry disabled sind.
     */
    busy?: boolean
}

export const MollieMethodsBlock: FC<MollieMethodsBlockProps> = ({
    courseId,
    selectedMethod,
    onMethodSelect,
    busy,
}) => {
    const { t } = useTranslation()
    const cardsRef = useRef<(HTMLDivElement | null)[]>([])
    const iconFallbackUsedRef = useRef<Set<string>>(new Set())

    // Mit `courseId` filtert das Backend die Methodenliste kursabhaengig (leere Liste bei
    // Nicht-Mollie-Kursen). Ohne `courseId` bleibt das bisherige Verhalten erhalten.
    const { data, loading: queryLoading, error: queryError } = useGetAvailablePaymentMethodsFromEndUserAppQuery({
        variables: courseId ? { courseId } : undefined,
        fetchPolicy: "cache-first",
    })

    // Geraete-Gating (gemeinsamer Helfer, konsistent zu BookingSummary-Count +
    // SummaryRight): Apple Pay nur auf Apple-Geraeten/Safari zeigen (sonst Sackgasse
    // auf Mollies Hosted-Page). Google Pay & alle uebrigen Methoden bleiben unveraendert.
    const methods = gateMollieMethodsForDevice(data?.getAvailablePaymentMethodsFromEndUserApp)

    // Loading state: show skeleton cards
    if (queryLoading) {
        return (
            <div>
                <h3 id="mollie-selector-title" className={css.sectionHeader}>
                    {t("mollieCheckout.selector.title")}
                </h3>
                <div className={css.grid}>
                    <Skeleton.Button active block style={{ height: 72, borderRadius: 7.5 }} />
                    <Skeleton.Button active block style={{ height: 72, borderRadius: 7.5 }} />
                </div>
            </div>
        )
    }

    // Error or empty: hide block completely (CHECKOUT-05, D-03)
    if (queryError) {
        // eslint-disable-next-line no-console
        console.warn("Mollie methods query failed", { error: queryError })
        return null
    }

    if (!methods.length) {
        return null
    }

    const handleCardClick = (methodId: string) => {
        // Gesperrte Karten dürfen auch per Tastatur nichts auslösen (pointer-events greift nur bei der Maus).
        if (busy) return
        onMethodSelect(methodId)
    }

    const handleKeyDown = (e: React.KeyboardEvent, methodId: string, index: number) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault()
            handleCardClick(methodId)
        } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault()
            const nextIndex = (index + 1) % methods.length
            cardsRef.current[nextIndex]?.focus()
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault()
            const prevIndex = (index - 1 + methods.length) % methods.length
            cardsRef.current[prevIndex]?.focus()
        }
    }

    const handleIconError = (e: React.SyntheticEvent<HTMLImageElement>, methodId: string) => {
        // Reihenfolge (Phase 9-UI-Hybrid):
        // 1. Primary = Mollie-CDN (`mollieCdnIconUrl(methodId)`)
        // 2. Fallback = lokale SVG aus `LOCAL_FALLBACK_ICONS`
        // WR-01-Guard (iconFallbackUsedRef) verhindert Endlosschleifen, wenn
        // der Fallback selbst 404/CSP-geblockt ist.
        const fallback = LOCAL_FALLBACK_ICONS[methodId]

        if (fallback && !iconFallbackUsedRef.current.has(methodId)) {
            iconFallbackUsedRef.current.add(methodId)
            e.currentTarget.src = fallback
        } else {
            e.currentTarget.style.display = "none"
        }
    }

    return (
        <div>
            <h3 id="mollie-selector-title" className={css.sectionHeader}>
                {t("mollieCheckout.selector.title")}
            </h3>
            <div
                className={css.grid}
                role="radiogroup"
                aria-labelledby="mollie-selector-title"
            >
                {methods.map((method, index) => {
                    const isSelected = selectedMethod === method.id
                    const methodLabel =
                        method.id === "banktransfer"
                            ? t("mollieCheckout.selector.methods.banktransfer", "Überweisung")
                            : method.description || method.id
                    // Primary-Quelle: offizielle Mollie-CDN-URL. Falls der Server in
                    // `method.imageUrl` bereits eine Mollie-URL liefert, nutzen wir die
                    // (sie ist identisch) — sonst bauen wir sie selbst.
                    const primaryIcon = method.imageUrl || mollieCdnIconUrl(method.id)

                    return (
                        <div
                            key={method.id}
                            ref={(el) => { cardsRef.current[index] = el }}
                            className={[
                                css.card,
                                isSelected ? css.cardSelected : "",
                                busy ? css.cardDisabled : "",
                            ].join(" ").trim()}
                            role="radio"
                            aria-checked={isSelected}
                            aria-disabled={busy || undefined}
                            tabIndex={isSelected || (!selectedMethod && index === 0) ? 0 : -1}
                            onClick={() => handleCardClick(method.id)}
                            onKeyDown={(e) => handleKeyDown(e, method.id, index)}
                            data-testid={`mollie-method-${method.id}`}
                        >
                            <img
                                className={css.icon}
                                src={primaryIcon}
                                alt={methodLabel}
                                onError={(e) => handleIconError(e, method.id)}
                            />
                            <span className={css.label}>
                                {methodLabel}
                            </span>
                            {isSelected && (
                                <CheckCircleFilled
                                    style={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        fontSize: 20,
                                        color: "#0760ff",
                                    }}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default MollieMethodsBlock
